import { faSpinner, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from 'Body/Statuses/Loading';
import React, { useContext, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Table } from 'reactstrap';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import Papa from 'papaparse';
import { useAuth0 } from '../../../../auth0/reactAuth0Spa';
import { checkLoginForError, formatBearerToken } from '../../../../Common.functions';
import { DepartmentContext } from '../../../../DepartmentWrapper';
import { postDataAgnostic } from '../../../../Services/dataApi';

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.onabort = reject;
    reader.readAsText(file);
  });

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.onabort = reject;
    reader.readAsArrayBuffer(file);
  });

const normalizeSpreadsheetCell = (value) => {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    if (typeof value.text === 'string') return value.text;
    if (Array.isArray(value.richText)) return value.richText.map((item) => item.text || '').join('');
    if (value.result != null) return String(value.result);
    if (typeof value.hyperlink === 'string') return value.hyperlink;
    return String(value);
  }

  return String(value);
};

const trimTrailingEmptyCells = (row = []) => {
  let lastNonEmptyIndex = row.length - 1;
  while (lastNonEmptyIndex >= 0 && (!row[lastNonEmptyIndex] || row[lastNonEmptyIndex].trim() === '')) {
    lastNonEmptyIndex--;
  }
  return row.slice(0, lastNonEmptyIndex + 1);
};

const parseSpreadsheetRows = async (file) => {
  const upperFileName = (file?.name || '').toUpperCase();
  const isCsv = upperFileName.endsWith('.CSV');
  const isXlsx = upperFileName.endsWith('.XLSX');

  if (isCsv) {
    const fileText = await readFileAsText(file);
    const parsed = Papa.parse(fileText, { skipEmptyLines: true });

    if (parsed.errors && parsed.errors.length > 0) {
      throw new Error('Unable to parse CSV file');
    }

    return (parsed.data || []).map((row) => row.map((cell) => normalizeSpreadsheetCell(cell)));
  }

  if (isXlsx) {
    const workbook = new ExcelJS.Workbook();
    const data = await readFileAsArrayBuffer(file);
    await workbook.xlsx.load(data);
    const worksheet = workbook.worksheets[0];
    if (!worksheet) return [];

    const rowCount = worksheet.rowCount || 0;
    const columnCount = worksheet.columnCount || 0;
    const rows = [];

    for (let rowIndex = 1; rowIndex <= rowCount; rowIndex++) {
      const row = worksheet.getRow(rowIndex);
      const currentRow = [];

      for (let columnIndex = 1; columnIndex <= columnCount; columnIndex++) {
        currentRow.push(normalizeSpreadsheetCell(row.getCell(columnIndex).value));
      }

      rows.push(trimTrailingEmptyCells(currentRow));
    }

    return rows;
  }

  throw new Error('Unsupported file type');
};

const AdminBulkImportUsers = ({ callback, newUserTemplate }) => {
  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const [postResults, setPostResults] = useState({});
  const [error, setError] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [finished, setFinished] = useState(false);
  const dContext = useContext(DepartmentContext);
  const panelStyle = {
    border: '1px solid #d9dee7',
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    padding: 16,
  };
  const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };
  const helperTextStyle = {
    fontSize: 12,
    color: '#54606e',
    lineHeight: 1.5,
    marginBottom: 12,
  };
  const compactTableStyle = {
    fontSize: 12,
    marginBottom: 0,
  };

  const postUsers = (users) => {
    getTokenSilently()
      .then((token) => {
        postDataAgnostic(
          'department/users',
          { pk_Department: dContext.department.pk_Department, isSingleUser: false },
          { users },
          formatBearerToken(token),
        )
          .then((result) => {
            setPostResults(result.data);
            setFinished(true);
            callback();
          })
          .catch((err) => {
            setError(true);
          })
          .finally(() => {
            setIsPosting(false);
          });
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const formatDataForPosting = (headerInfo, body) => {
    return body.map((bodyItem) => {
      const entry = {};
      bodyItem.forEach((item, i) => {
        if (
          headerInfo[i] === 'EnableEvaluator' ||
          headerInfo[i] === 'EnableAssistant' ||
          headerInfo[i] === 'EnablePointOfContact' ||
          headerInfo[i] === 'EnableAdmin'
        ) {
          entry[headerInfo[i]] = item.trim().length > 0 && item.trim().toUpperCase() !== 'NO' ? 1 : 0;
        } else {
          if (item.length > 0) entry[headerInfo[i]] = item;

          // need to add email to this key
          if (headerInfo[i] === 'UserEmail') {
            entry['Auth0Email'] = item;
          }
        }
      });
      return entry;
    });
  };
  const getHeaderData = (arrayOfData) => {
    const keys = {};
    if (arrayOfData && arrayOfData[1]) {
      const headerPosition = arrayOfData[1].forEach((item, i) => {
        const itemToUpperCase = item.toUpperCase();
        switch (true) {
          case itemToUpperCase.includes('EMAIL'):
            keys[i] = 'UserEmail';
            break;
          case itemToUpperCase.includes('LAST'):
            keys[i] = 'UserLast';
            break;
          case itemToUpperCase.includes('FIRST'):
            keys[i] = 'UserFirst';
            break;
          case itemToUpperCase.includes('MIDDLE'):
            keys[i] = 'UserMiddle';
            break;
          case itemToUpperCase.includes('SUFFIX'):
            keys[i] = 'UserSuffix';
            break;
          case itemToUpperCase.includes('CELL'):
            keys[i] = 'UserCellPhoneNumber';
            break;
          case itemToUpperCase.includes('EVALUATOR'):
            keys[i] = 'EnableEvaluator';
            break;
          case itemToUpperCase.includes('ADMINISTRATOR'):
            keys[i] = 'EnableAdmin';
            break;
          case itemToUpperCase.includes('POINT OF CONTACT'):
            keys[i] = 'EnablePointOfContact';
            break;
        }
      });
      return keys;
    }
  };

  const handleSpreadsheetDrop = async (file) => {
    const rows = await parseSpreadsheetRows(file);
    const headerInfo = getHeaderData(rows);

    if (!headerInfo) {
      throw new Error('Unable to locate required headers');
    }

    // Remove the description/header rows from the template.
    const arrayWithoutHeader = rows.filter((item, i) => i > 1);
    const formatedForPosting = formatDataForPosting(headerInfo, arrayWithoutHeader);
    postUsers(formatedForPosting);
  };

  const onDrop = async (files) => {
    setIsPosting(true);
    const file = files[0];
    if (!file || (!file.name.toUpperCase().endsWith('.XLSX') && !file.name.toUpperCase().endsWith('.CSV'))) {
      Swal.fire('Incompatible File Type', 'Please make sure your file is either of type .xlsx or .csv', 'error');
      setIsPosting(false);
      return;
    }

    try {
      await handleSpreadsheetDrop(file);
    } catch (err) {
      Swal.fire(
        'Unable to Parse File',
        'Data file is not formatted properly, please reformat data or contact support.',
        'error',
      );
      setIsPosting(false);
    }
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  const { existingUsers, newCandidates } = postResults;
  const addedUserCount = newCandidates ? newCandidates.length : 0;

  if (finished) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ ...panelStyle, maxHeight: '55vh', overflow: 'auto' }}>
          {newCandidates.length > 0 && (
            <div>
              <div style={{ width: '100%', textAlign: 'center', marginBottom: 10 }}>
                <h5 style={{ color: '#009944', fontSize: 14, marginBottom: 0 }}>
                  {`${addedUserCount} User${addedUserCount === 1 ? '' : 's'} Added`}
                </h5>
              </div>
              <Table striped responsive size="sm" style={compactTableStyle}>
                <tbody>
                  {newCandidates.map((item) => {
                    const {
                      Auth0Email,
                      EnableAssistant,
                      EnableEvaluator,
                      EnablePointOfContact,
                      UserCellPhoneNumber,
                      UserEmail,
                      UserFirst,
                      UserLast,
                      UserMiddle,
                    } = item;
                    return (
                      <tr>
                        <td>{UserFirst}</td>
                        <td>{UserLast}</td>
                        <td>{UserEmail}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
          {existingUsers.length > 0 && (
            <div>
              <div style={{ width: '100%', textAlign: 'center', marginBottom: 10, marginTop: newCandidates.length > 0 ? 18 : 0 }}>
                <h5 style={{ color: '#CF000F', fontSize: 14, marginBottom: 0 }}>Users With These Emails Already Exist</h5>
              </div>
              <Table striped responsive size="sm" style={compactTableStyle}>
                <tbody>
                  {existingUsers.map((item) => {
                    const {
                      Auth0Email,
                      EnableAssistant,
                      EnableEvaluator,
                      EnablePointOfContact,
                      UserCellPhoneNumber,
                      UserEmail,
                      UserFirst,
                      UserLast,
                      UserMiddle,
                    } = item;
                    return (
                      <tr>
                        <td>{UserFirst}</td>
                        <td>{UserLast}</td>
                        <td>{UserEmail}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={(e) => {
              e.preventDefault();
              setFinished(false);
              setPostResults({});
            }}
            color="info"
          >
            New Import
          </Button>
        </div>
      </div>
    );
  }
  return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={panelStyle}>
          <div style={{ ...sectionTitleStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>Bulk Import Users</span>
            <a
              href={newUserTemplate}
              target="_blank"
              rel="noopener noreferrer"
              download="New_User_Template.xlsx"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#0d6efd',
                color: 'white',
                borderRadius: 4,
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 'bold',
                textDecoration: 'none',
                lineHeight: 1.2,
                textTransform: 'none',
                letterSpacing: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Download Bulk User Template
            </a>
          </div>
          <div style={helperTextStyle}>
            Upload the completed team template as a <b>.xlsx</b> or <b>.csv</b> file. Export Apple Numbers files to
            Excel first before importing.
        </div>
      </div>
      <div
        style={{
          cursor: 'pointer',
          minHeight: '240px',
          border: '2px dashed #9fb3c8',
          borderRadius: 10,
          backgroundColor: '#f8fafc',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20,
        }}
        className="hover_blue"
      >
        {isPosting && (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <div style={{ height: 80, width: 80 }}>
              <Loading options={{ labelText: 'Uploading. . .', labelStyle: { fontSize: 9, textAlign: 'center' } }} />
            </div>

            {/* <FontAwesomeIcon icon={faSpinner} spin size="3x" /> */}
          </div>
        )}
        {!isPosting && (
          <div {...getRootProps()} style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <input {...getInputProps()} />
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', flexDirection: 'column', textAlign: 'center' }}>
              <FontAwesomeIcon icon={faUpload} size="4x" style={{ color: '#59718a' }} />
              <p style={{ paddingTop: '15px', marginBottom: 6, fontSize: 14, fontWeight: 'bold' }}>
                Drag and drop a CSV or XLSX file
              </p>
              <p style={{ marginBottom: 0, fontSize: 12, color: '#54606e' }}>
                Click anywhere in this box to browse for a file
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBulkImportUsers;
